import {z} from 'zod'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {useTranslation} from '@/translations/i18n'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {MedicalReportFieldsSchema} from '@/features/medical-reports/formSchemas'
import {InputSelectReportType} from '@/features/medical-reports/components/input-select-report-type/InputSelectReportType'
import {useGetPreviousReportByProject} from '@/features/medical-reports/queries/useGetPreviousReportByProject'
import {useResetMedicalReportForm} from '@/features/medical-reports/hooks/useResetMedicalReportForm'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Switch} from '@/components/commons/switch/Switch'
import {Text} from '@/components/ui/text/Text'
import {InputSelectAccesses} from '../input-select-accesses/InputSelectAccesses'
import {Access} from '@/features/accesses/types'
import dayjs from 'dayjs'
import {MedicalReportTypeKeyName} from '@/types'
import {useAuthStore} from '@/features/auth/store/store'

const MedicalReportTypeValidationSchema = MedicalReportFieldsSchema.pick({
    reportDate: true,
    reportType: true,
    reportTypeKeyName: true,
    project: true,
    notes: true,
    accesses: true,
    isReportAccessDetatched: true,
    teamMembers: true,
    newProjectStartDate: true,
    newProjectDurationDays: true
})
type MedicalReportTypeValidationSchema = z.infer<typeof MedicalReportTypeValidationSchema>

export const MedicalReportTypeAndAccessSection = ({isDisabled}: {isDisabled: boolean}) => {
    const {t} = useTranslation()
    return (
        <FormSection title={t('medical_records:report_type_and_access')} hasBottomDivider>
            <Grid columns={3}>
                <GridItem spanColumn={2}>
                    <MedicalReportTypeField isDisabled={isDisabled} />
                </GridItem>
            </Grid>
        </FormSection>
    )
}

const MedicalReportTypeField = ({isDisabled}: {isDisabled: boolean}) => {
    const {t} = useTranslation()
    const {user} = useAuthStore()
    const form = useFormContext<MedicalReportTypeValidationSchema>()
    const medicalReportType = useWatch({control: form.control, name: 'reportType'})
    const project = useWatch({control: form.control, name: 'project'})
    const reportDate = useWatch({control: form.control, name: 'reportDate'})
    const patientId = useWatch({control: form.control, name: 'project.patientId'})

    // In case of starting project report, we do not show the previous report button
    const isPreviousVersionEnabled =
        medicalReportType?.keyName != 'start_continuous_project' &&
        medicalReportType?.keyName != 'start_cyclical_project'

    // looking for previous reports
    useGetPreviousReportByProject({
        projectId: project?.id,
        medicalReportTypeId: medicalReportType?.id,
        options: {
            enabled: !!project?.id && !!medicalReportType?.id && isPreviousVersionEnabled
        }
    })

    const resetMedicalReportForm = useResetMedicalReportForm({
        fieldsToKeep: [
            'reportDate',
            'project',
            'medicalRecordId',
            'reportType',
            'reportTypeKeyName',
            'status',
            'teamMembers'
        ],
        reportDate,
        currentReportType: medicalReportType,
        projectRefId: project?.projectRefId || undefined
    })

    const onChangeAccessSideEffect = (accesses: Access[]) => {
        const teamMembers = accesses[0].event?.eventTeams

        form.setValue(
            'teamMembers',
            teamMembers.map(teamMember => ({
                id: teamMember.member.userId,
                group: teamMember.member.group,
                fullName: teamMember.member.fullName,
                specializationName: teamMember.member.specializationName,
                isAReserve: teamMember.member.isAReserve
            })),
            {shouldDirty: true}
        )

        form.setValue('reportDate', new Date(accesses[0].event.date), {shouldDirty: true})
    }

    const isReportAccessDetatched = useWatch({control: form.control, name: 'isReportAccessDetatched'})
    const accesses = useWatch({control: form.control, name: 'accesses'})

    const isReportAccessDetatchedesSideEffect = (isReportAccessDetatched: boolean) => {
        form.resetField('accesses', {defaultValue: undefined})
        form.setValue('teamMembers', project?.team || [], {shouldDirty: true})
        form.trigger('accesses')
        if (isReportAccessDetatched && !!accesses) {
            form.setValue('reportDate', new Date(), {shouldDirty: true})
        }
    }

    const onChangeReportTypeSideEffect = (reportType: MedicalReportTypeKeyName) => {
        if (reportType == 'continuous_project_extension_request') {
            const targetEndDate = project?.endDate || project?.expirationDate
            const projectDurationDays = project?.durationDays || 0

            if (projectDurationDays && targetEndDate) {
                form.setValue(
                    'newProjectStartDate',
                    targetEndDate ? dayjs(targetEndDate).add(1, 'day').toDate() : dayjs().toDate(),
                    {
                        shouldDirty: true
                    }
                )

                form.setValue('newProjectDurationDays', Number(projectDurationDays), {shouldDirty: true})
            }
        }
    }

    return (
        <Flexbox direction="column" gap={6} fullWidth>
            <Controller
                control={form.control}
                name="reportType"
                render={({field, fieldState}) => (
                    <InputSelectReportType
                        label={t('medical_records:report_type')}
                        value={field.value}
                        errorMessage={fieldState.error?.message}
                        disabled={isDisabled}
                        specializationId={
                            user?.roleName !== 'Admin' && user?.specializationId ? user.specializationId : undefined
                        }
                        onChange={value => {
                            if (value?.keyName) {
                                field.onChange(value)
                                form.setValue('reportTypeKeyName', value.keyName)
                                form.resetField('accesses', {defaultValue: undefined})
                                if (value.keyName === 'team_report' || value.keyName === 'glo') {
                                    form.setValue('teamMembers', project?.team || [], {shouldDirty: true})
                                }
                                resetMedicalReportForm(value)
                                onChangeReportTypeSideEffect(value.keyName)
                            }
                        }}
                    />
                )}
            />
            <Flexbox gap={2} align="center">
                <Controller
                    control={form.control}
                    name="isReportAccessDetatched"
                    render={({field}) => (
                        <Switch
                            size="sm"
                            checked={field.value}
                            disabled={isDisabled}
                            onChange={e => {
                                field.onChange(e)
                                isReportAccessDetatchedesSideEffect(e)
                            }}
                        />
                    )}
                />
                <Text size="textSm" weight={500}>
                    {t('medical_records:no_access_type')}
                </Text>
            </Flexbox>
            <Controller
                control={form.control}
                name="accesses"
                render={({field, fieldState}) => (
                    <InputSelectAccesses
                        label={t('medical_records:access_type_label')}
                        mode="multiple"
                        value={field.value}
                        onChange={values => {
                            field.onChange(values)
                            if (values) {
                                onChangeAccessSideEffect(values)
                            }
                        }}
                        errorMessage={fieldState.error?.message}
                        disabled={isDisabled || isReportAccessDetatched || !medicalReportType.id || !patientId}
                        queryParams={{
                            medicalRecordReportType: medicalReportType?.id,
                            patient: patientId,
                            project: project?.id,
                            hasReport: false
                        }}
                    />
                )}
            />
        </Flexbox>
    )
}
